// DOM rendering for validation reports, ported from the Waypoint Studios
// design prototype. No framework — just createElement — which keeps the
// bundle small and the Pages deploy trivial.

import type { CheckResult, ProductType, ValidationReport } from "@waypoint/validator-core";
import type { ValidateResponse } from "./api.js";

const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  skin_pack: "Skin Pack",
  world_template: "World Template",
  // A `dynamic_world_template` is reported alongside `world_template`,
  // so when both appear in the detected list we prefer the dynamic
  // label in the product pill (handled in renderResultHeader).
  dynamic_world_template: "Dynamic World Template",
  addon: "Add-On",
  // "Texture Pack" matches the Marketplace category name shown to end users,
  // even though the underlying manifest uses module type "resources".
  resource_pack: "Texture Pack",
  persona: "Persona",
  emote: "Emote",
  unknown: "Unknown product",
};

/**
 * Rule IDs that the browser-side auto-fixer (web/src/fixer.ts) knows how to
 * resolve. Keep in sync with fixer.ts: any new fix there should add its
 * corresponding rule id here so the badge + fix-card summary stay accurate.
 */
const AUTOFIXABLE_RULE_IDS = new Set<string>([
  // Root-layout enforcement (strip wrappers + drop extras).
  "pack.root-layout",
  // Packaged-format file removal.
  "pack.no-packaged-formats",
  "pack.no-mcpack-in-content",
  "pack.no-mcpack-in-marketing-art",
  // Foreign-asset file removal (.mcmeta / .hdr).
  "pack.no-foreign-asset-formats",
  // Bedrock backup file removal (.dat_old).
  "pack.no-dat-old",
  // Nested archive / authoring-source removal (.zip / .bbmodel /
  // Content-scoped .psd). All exempt persona + emote.
  "pack.no-nested-zip",
  "pack.no-bbmodel",
  "pack.no-psd-in-content",
  // JSON BOM stripping.
  "pack.json-no-bom",
  // Junk-file removal.
  "pack.no-macos-metadata",
  "pack.no-macos-applefork",
  "pack.no-macosx-folder",
  "pack.no-thumbs-db",
  "pack.no-source-maps",
  // Mutually-exclusive art naming canonicalisation.
  "art.consistent-store-screenshot-naming",
  "art.consistent-panorama-naming",
  // Skin pack asset renames.
  "skin.png-model-tag",
  // Marketing screenshot index normalisation (renumber to 0-indexed).
  "art.marketing-screenshot-sequence",
]);

function isAutoFixable(r: CheckResult): boolean {
  return AUTOFIXABLE_RULE_IDS.has(r.ruleId);
}

export interface ReportCallbacks {
  onFix?: () => void;
  onReset?: () => void;
  onShare?: () => void;
  onDownloadPdf?: () => void;
}

export interface RenderData {
  response: ValidateResponse;
  sha256: string;
  durationMs: number;
  /** When set, display the report as a read-only shared view. */
  shared?: {
    createdAt: string;
    expiresAt: string;
  };
}

// -------------------- Upload view --------------------

export interface UploadCallbacks {
  onFile: (file: File) => void;
}

export interface UploadCopy {
  /** `accept` attribute for the file input (e.g. ".zip,application/zip"). */
  accept?: string;
  /** Heading shown above the dropzone. */
  heading?: string;
  /** HTML for the sub-line beneath the heading. */
  subHtml?: string;
}

export function renderUpload(
  container: HTMLElement,
  cb: UploadCallbacks,
  copy: UploadCopy = {},
): void {
  container.innerHTML = "";

  const zone = el("label", { class: "upload", for: "file-input", tabindex: "0" });
  const input = document.createElement("input");
  input.type = "file";
  input.id = "file-input";
  input.accept = copy.accept ?? ".zip,application/zip";
  input.hidden = true;
  input.addEventListener("change", () => {
    const f = input.files?.[0];
    if (f) cb.onFile(f);
  });

  const icon = el("div", { class: "upload-icon" });
  icon.innerHTML = iconSvg(
    '<path d="M12 15V4"/><path d="m7 9 5-5 5 5"/><path d="M4 17v1a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1"/>',
  );

  const heading = el("h2", {}, copy.heading ?? "Drop your pack to validate");
  const sub = el("p");
  sub.innerHTML = copy.subHtml ?? 'Supports <span class="mono">.zip</span> up to 500&nbsp;MB.';

  const button = el("button", { type: "button", class: "btn primary" });
  button.innerHTML =
    iconSvg(
      '<path d="M12 15V4"/><path d="m7 9 5-5 5 5"/><path d="M4 17v1a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1"/>',
      16,
    ) + "Choose a file";
  button.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    input.click();
  });

  zone.append(input, icon, heading, sub, button);

  zone.addEventListener("dragenter", (e) => {
    e.preventDefault();
    zone.classList.add("drag");
  });
  zone.addEventListener("dragover", (e) => {
    e.preventDefault();
    zone.classList.add("drag");
  });
  zone.addEventListener("dragleave", () => zone.classList.remove("drag"));
  zone.addEventListener("drop", (e) => {
    e.preventDefault();
    zone.classList.remove("drag");
    const f = e.dataTransfer?.files?.[0];
    if (f) cb.onFile(f);
  });
  zone.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      input.click();
    }
  });

  container.append(zone);
}

// -------------------- Scanning view --------------------

export interface ScanningState {
  fileName: string;
  progress: number;
  label: string;
  lines: string[];
}

export function renderScanning(container: HTMLElement, s: ScanningState): void {
  // Reuse existing scanning node if already rendered to avoid flicker.
  let zone = container.querySelector<HTMLElement>(".upload.busy");
  if (!zone) {
    container.innerHTML = "";
    zone = el("div", { class: "upload busy" });
    const icon = el("div", { class: "upload-icon" });
    icon.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="9" opacity="0.15" />
        <path d="M3 12a9 9 0 0 1 9-9">
          <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
        </path>
      </svg>`;
    const h = el("h2");
    const wrap = el("div", { class: "progress-wrap" });
    const bar = el("div", { class: "progress-bar" });
    const fill = el("div", { class: "fill" });
    bar.append(fill);
    const plabel = el("div", { class: "progress-label" });
    const tag = el("span", { class: "tag" });
    const pct = el("span");
    plabel.append(tag, pct);
    const lines = el("div", { class: "scanning-lines" });
    wrap.append(bar, plabel, lines);
    zone.append(icon, h, wrap);
    container.append(zone);
  }

  const h = zone.querySelector<HTMLElement>("h2")!;
  h.innerHTML = `Validating <span class="mono" style="font-size:14px;font-weight:500">${escapeHtml(
    s.fileName,
  )}</span>`;

  const fill = zone.querySelector<HTMLElement>(".progress-bar .fill")!;
  fill.style.width = `${Math.max(0, Math.min(100, s.progress))}%`;

  const plabel = zone.querySelector<HTMLElement>(".progress-label")!;
  plabel.innerHTML = `<span class="tag">${escapeHtml(s.label.toUpperCase())}</span><span>${Math.round(s.progress)}%</span>`;

  const lines = zone.querySelector<HTMLElement>(".scanning-lines")!;
  lines.innerHTML = "";
  for (const line of s.lines) {
    lines.append(el("div", { class: "line" }, `▸ ${line}`));
  }
}

// -------------------- Results --------------------

export function renderReport(
  container: HTMLElement,
  data: RenderData,
  cb: ReportCallbacks = {},
): void {
  container.innerHTML = "";

  const root = el("div", { class: "results" });

  const { response, sha256, durationMs, shared } = data;
  const report = response.report;
  const total = report.summary.total;
  const tone =
    report.failed.length > 0 ? "bad" : report.warnings.length > 0 ? "warn" : "";

  root.append(
    renderResultHeader(response, sha256, durationMs, tone, {
      onReset: cb.onReset,
      onShare: cb.onShare,
      onDownloadPdf: cb.onDownloadPdf,
      shared,
    }),
  );
  if (shared) {
    root.append(renderExpiryBanner(shared));
  }
  root.append(renderStats(report));

  const hasIssues = report.failed.length > 0 || report.warnings.length > 0;
  // The fix card only makes sense while the user still has the original
  // upload in memory. Shared viewers never see it.
  if (hasIssues && cb.onFix && !shared) {
    const fixable = [...report.failed, ...report.warnings].filter(isAutoFixable);
    root.append(renderFixCard(report, fixable.length, cb.onFix));
  }

  if (report.failed.length > 0) {
    root.append(
      renderSection({
        tone: "failed",
        title: "Failed checks",
        count: `${report.failed.length} issue${report.failed.length === 1 ? "" : "s"}`,
        items: report.failed,
        render: (r) => renderCheck(r, "fail"),
        defaultOpen: true,
      }),
    );
  }
  if (report.warnings.length > 0) {
    root.append(
      renderSection({
        tone: "warn",
        title: "Warnings",
        count: `${report.warnings.length} item${report.warnings.length === 1 ? "" : "s"}`,
        items: report.warnings,
        render: (r) => renderCheck(r, "warn"),
        defaultOpen: true,
      }),
    );
  }
  if (report.passed.length > 0) {
    root.append(
      renderSection({
        tone: "passed",
        title: "Passed checks",
        count: `${report.passed.length} passed`,
        items: report.passed,
        render: (r) => renderPassRow(r),
        defaultOpen: false,
      }),
    );
  }

  container.append(root);
  void total;
}

function renderExpiryBanner(shared: {
  createdAt: string;
  expiresAt: string;
}): HTMLElement {
  const banner = el("div", { class: "expiry-banner" });
  const expiresAt = new Date(shared.expiresAt);
  const formatted = formatExpiryGmt(expiresAt);
  const remainingMs = expiresAt.getTime() - Date.now();
  const remainingLabel = humanDuration(remainingMs);

  banner.innerHTML = `
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
    <div>
      <div class="expiry-title">Shared report</div>
      <div class="expiry-sub">Expires <b>${escapeHtml(formatted)}</b> · ${escapeHtml(remainingLabel)} remaining</div>
    </div>
  `;
  return banner;
}

function formatExpiryGmt(date: Date): string {
  // 2026-04-25 14:32 GMT
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  const hh = String(date.getUTCHours()).padStart(2, "0");
  const mm = String(date.getUTCMinutes()).padStart(2, "0");
  return `${y}-${m}-${d} ${hh}:${mm} GMT`;
}

function humanDuration(ms: number): string {
  if (ms <= 0) return "expired";
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  if (days >= 1) return `${days}d ${hours}h`;
  if (hours >= 1) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

interface HeaderCallbacks {
  onReset?: () => void;
  onShare?: () => void;
  onDownloadPdf?: () => void;
  shared?: { createdAt: string; expiresAt: string };
}

function renderResultHeader(
  response: ValidateResponse,
  sha256: string,
  durationMs: number,
  tone: string,
  cb: HeaderCallbacks = {},
): HTMLElement {
  const wrap = el("div", { class: "result-header" });
  const meta = el("div", { class: "file-meta" });

  const fileIcon = el("div", { class: "file-icon" });
  fileIcon.innerHTML = iconSvg(
    '<path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M14 3v6h6"/>',
    22,
  );

  const info = el("div");
  const name = el("h2", { class: "file-name" });
  const nameSpan = el("span", { class: "mono", style: "font-size:18px;font-weight:600" });
  nameSpan.textContent = response.file.name;
  name.append(nameSpan);

  let detectedTypes =
    response.report.productTypes.length > 0
      ? response.report.productTypes
      : (["unknown"] as ProductType[]);
  // dynamic_world_template implies world_template — collapse to just
  // the dynamic label when both are present so the pill reads
  // "Dynamic World Template" instead of the redundant
  // "World Template + Dynamic World Template".
  if (
    detectedTypes.includes("dynamic_world_template") &&
    detectedTypes.includes("world_template")
  ) {
    detectedTypes = detectedTypes.filter((t) => t !== "world_template");
  }
  // For persona packages, append the resolved category to the
  // product pill (e.g. "Persona: Headwear") so the kind of piece is
  // visible at a glance without diving into the report.
  const personaCategory = response.report.detection.personaCategory;
  const productLabel = detectedTypes
    .map((t) =>
      t === "persona" && personaCategory
        ? `Persona: ${personaCategory.label}`
        : PRODUCT_TYPE_LABELS[t],
    )
    .join(" + ");
  const pill = el("span", { class: "product-pill" });
  pill.append(el("span", { class: "pix" }), document.createTextNode(productLabel));
  name.append(pill);

  const details = el("div", { class: "file-details" });
  details.append(
    formatDetailSpan("Size", formatBytes(response.file.size)),
    formatDetailSpan("SHA-256", shortSha(sha256)),
    formatDetailSpan("Duration", formatDuration(durationMs)),
  );

  const actions = el("div", { class: "header-actions" });

  if (cb.onShare) {
    const btn = el("button", {
      type: "button",
      class: "btn primary small",
    });
    btn.innerHTML =
      iconSvg(
        '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>',
        14,
      ) + "Share";
    btn.addEventListener("click", () => cb.onShare?.());
    actions.append(btn);
  }

  if (cb.onDownloadPdf) {
    const btn = el("button", {
      type: "button",
      class: "btn ghost small",
    });
    btn.innerHTML =
      iconSvg(
        '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>',
        14,
      ) + "Download PDF";
    btn.addEventListener("click", () => cb.onDownloadPdf?.());
    actions.append(btn);
  }

  if (cb.onReset) {
    const resetBtn = el("button", {
      type: "button",
      class: "btn ghost small",
    });
    resetBtn.innerHTML =
      iconSvg(
        '<polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>',
        14,
      ) + "Validate another";
    resetBtn.addEventListener("click", () => cb.onReset?.());
    actions.append(resetBtn);
  }

  if (actions.children.length > 0) details.append(actions);

  info.append(name, details);
  meta.append(fileIcon, info);

  const score = el("div", { class: "score" });
  const scoreVal = el(
    "span",
    { class: `score-val ${tone}`.trim() },
    `${response.report.summary.passed}/${response.report.summary.total}`,
  );
  const scoreLbl = el("span", { class: "score-lbl" });
  scoreLbl.innerHTML = "checks<br>passed";
  score.append(scoreVal, scoreLbl);

  wrap.append(meta, score);
  return wrap;
}

function formatDetailSpan(label: string, value: string): HTMLElement {
  const span = el("span");
  span.innerHTML = `<b>${label}</b> `;
  span.append(document.createTextNode(value));
  return span;
}

function renderStats(report: ValidationReport): HTMLElement {
  const stats = el("div", { class: "stats" });
  stats.append(statCard("total", "Total checks", String(report.summary.total)));
  stats.append(statCard("passed", "Passed", String(report.summary.passed), true));
  stats.append(statCard("warn", "Warnings", String(report.summary.warnings), true));
  stats.append(statCard("failed", "Failed", String(report.summary.failed), true));
  return stats;
}

function statCard(tone: string, label: string, value: string, swatch = false): HTMLElement {
  const card = el("div", { class: `stat ${tone}` });
  const k = el("div", { class: "k" });
  if (swatch) k.append(el("span", { class: "swatch" }));
  k.append(document.createTextNode(` ${label}`));
  const v = el("div", { class: "v" }, value);
  card.append(k, v);
  return card;
}

function renderFixCard(
  report: ValidationReport,
  fixableCount: number,
  onFix: () => void,
): HTMLElement {
  const totalIssues = report.failed.length + report.warnings.length;
  const card = el("div", {
    class: `fix-card${fixableCount === 0 ? " unavailable" : ""}`,
  });
  const body = el("div");
  const title = el("div", { class: "fix-title" });

  if (fixableCount > 0) {
    title.append(document.createTextNode("Try the auto-fixer"));
    title.append(el("span", { class: "fix-tag experimental" }, "Experimental"));
    body.append(title);
    body.append(
      el(
        "div",
        { class: "fix-sub" },
        `The browser can rebuild your package with common fixes: strip wrapper folders, delete .DS_Store / Thumbs.db / .map files, normalise mixed screenshot / panorama spellings, and add the model tag (_slim / _custom) to skin PNGs based on skins.json. ${fixableCount} of ${totalIssues} issue${totalIssues === 1 ? "" : "s"} look auto-fixable.`,
      ),
    );
    const btn = el("button", { type: "button", class: "btn primary" });
    btn.textContent = `Attempt to fix (${fixableCount} issue${fixableCount === 1 ? "" : "s"})`;
    btn.addEventListener("click", () => onFix());
    card.append(body, btn);
    return card;
  }

  // No auto-fixable issues in this report — flip the card into an advisory
  // state so the user knows to open the per-check guided help instead of
  // waiting for a one-click rebuild that would be a no-op.
  title.append(document.createTextNode("Auto-fixer unavailable"));
  body.append(title);
  body.append(
    el(
      "div",
      { class: "fix-sub" },
      "The auto-fixer can't fix these issues yet. Try to manually fix them using our guided help.",
    ),
  );
  card.append(body);
  return card;
}

// -------------------- Sections --------------------

interface SectionOptions<T> {
  tone: "failed" | "warn" | "passed";
  title: string;
  count: string;
  items: T[];
  render: (item: T) => HTMLElement;
  defaultOpen: boolean;
}

function renderSection<T>(opts: SectionOptions<T>): HTMLElement {
  const section = el("section", {
    class: `section ${opts.tone}${opts.defaultOpen ? " open" : ""}`,
  });

  const header = el("button", { type: "button", class: "section-header" });
  header.append(el("span", { class: "status-dot" }));
  header.append(el("span", { class: "section-title" }, opts.title));
  header.append(el("span", { class: "section-count" }, opts.count));
  const chev = el("span", { class: "section-chev" });
  chev.innerHTML = iconSvg('<polyline points="6 9 12 15 18 9"/>', 18);
  header.append(chev);
  header.addEventListener("click", () => section.classList.toggle("open"));

  const body = el("div", { class: "section-body" });
  const list = el("div", { class: "check-list" });
  for (const it of opts.items) list.append(opts.render(it));
  body.append(list);

  section.append(header, body);
  return section;
}

// -------------------- Check items --------------------

function renderCheck(r: CheckResult, tone: "fail" | "warn"): HTMLElement {
  const row = el("div", { class: `check ${tone}` });
  const icon = el("div", { class: "check-icon" });
  icon.innerHTML =
    tone === "fail"
      ? iconSvg(
          '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
          12,
          3,
        )
      : iconSvg(
          '<line x1="12" y1="8" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
          12,
          3,
        );

  const main = el("div", { class: "check-main" });
  const title = el("div", { class: "check-title" });
  title.append(document.createTextNode(shortTitle(r)));
  title.append(el("span", { class: "rule-id" }, r.ruleId));
  if (isAutoFixable(r)) {
    title.append(el("span", { class: "autofix-chip" }, "Auto-fix compatible"));
  }
  main.append(title);
  main.append(el("p", { class: "check-reason" }, r.message));
  main.append(renderDetailBlock(r));

  const trailing = el("div");
  trailing.append(el("span", { class: "check-badge" }, severityLabel(r.severity)));

  row.append(icon, main, trailing);
  return row;
}

function renderPassRow(r: CheckResult): HTMLElement {
  const row = el("div", { class: "pass-row" });
  const icon = el("div", { class: "check-icon" });
  icon.innerHTML = iconSvg('<polyline points="20 6 9 17 4 12"/>', 12, 3);
  row.append(icon);
  row.append(el("div", { class: "title" }, r.description ?? r.message));
  row.append(el("div", { class: "id" }, r.ruleId));
  return row;
}

/** Build the structured "detail" block under a failing / warning check. */
function renderDetailBlock(r: CheckResult): HTMLElement {
  const block = el("div", { class: "check-detail" });
  if (r.filePath) block.append(detailRow("File", codeNode(r.filePath)));

  // Vanilla-audit results own their primary rendering because they need a
  // TGA filter and a download-as-txt button instead of the generic "Show N
  // more" list. Keys that the special renderer consumes are skipped below
  // so the generic loop doesn't duplicate them.
  const vanillaKind =
    r.details && typeof r.details.kind === "string" ? r.details.kind : null;
  const vanillaConsumedKeys = new Set<string>();
  if (vanillaKind === "vanillaIdentical" || vanillaKind === "vanillaMissing") {
    const primaryKey = vanillaKind === "vanillaIdentical" ? "matches" : "missing";
    const tgaKey = vanillaKind === "vanillaIdentical" ? "matchesTga" : "missingTga";
    const primary = asStringArray(r.details?.[primaryKey]);
    const tga = asStringArray(r.details?.[tgaKey]);
    if (primary.length > 0 || tga.length > 0) {
      block.append(
        detailRow(
          vanillaKind === "vanillaIdentical" ? "Identical files" : "Missing files",
          renderVanillaFileList(vanillaKind, primary, tga, r.details?.pack as string | undefined),
        ),
      );
      vanillaConsumedKeys.add(primaryKey);
      vanillaConsumedKeys.add(tgaKey);
      vanillaConsumedKeys.add("kind");
    }
  }

  if (r.details) {
    for (const [k, v] of Object.entries(r.details)) {
      if (v === null || v === undefined) continue;
      if (vanillaConsumedKeys.has(k)) continue;
      block.append(detailRow(toLabel(k), formatValueNode(v, k)));
    }
  }
  if (r.description) {
    block.append(detailRow("Rule intent", textNode(r.description)));
  }
  if (r.fix) {
    block.append(detailRow("Fix", textNode(r.fix)));
  }
  if (r.docs && r.severity !== "error") {
    const a = document.createElement("a");
    a.href = r.docs.href;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.textContent = `${r.docs.label} ↗`;
    block.append(detailRow("Docs", a));
  }
  return block;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string" && v.length > 0);
}

/**
 * Dedicated renderer for vanilla audit path lists. Includes:
 *   - the primary png/jpg/jpeg list (visible by default, with "Show N more"),
 *   - a "Include .tga (N)" checkbox that appends the .tga entries on demand,
 *   - a "Download .txt" button that exports whatever is currently visible.
 */
function renderVanillaFileList(
  kind: "vanillaIdentical" | "vanillaMissing",
  primary: string[],
  tga: string[],
  packName: string | undefined,
): HTMLElement {
  const wrap = el("div", { class: "vanilla-audit-list" });
  const list = el("div", { class: "vanilla-audit-list-body" });
  wrap.append(list);

  let includeTga = false;
  const render = (): void => {
    list.innerHTML = "";
    const active = includeTga ? [...primary, ...tga] : primary;
    if (active.length === 0) {
      list.append(el("div", { class: "muted" }, "None"));
      return;
    }
    // Reuse the existing collapsible array renderer for consistency.
    list.append(renderArrayList(active));
  };
  render();

  const controls = el("div", { class: "vanilla-audit-controls" });

  if (tga.length > 0) {
    const label = document.createElement("label");
    label.className = "vanilla-audit-toggle";
    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.addEventListener("change", () => {
      includeTga = checkbox.checked;
      render();
    });
    label.append(checkbox, document.createTextNode(` Include .tga (${tga.length})`));
    controls.append(label);
  }

  const downloadBtn = document.createElement("button");
  downloadBtn.type = "button";
  downloadBtn.className = "btn ghost small";
  downloadBtn.textContent = "Download .txt";
  downloadBtn.addEventListener("click", () => {
    const active = includeTga ? [...primary, ...tga] : primary;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const kindLabel = kind === "vanillaIdentical" ? "identical" : "missing";
    const safePack = (packName || "pack").replace(/[^a-z0-9_-]+/gi, "_");
    const filename = `${safePack}-${kindLabel}-${stamp}.txt`;
    const blob = new Blob([active.join("\n") + "\n"], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
  controls.append(downloadBtn);

  wrap.append(controls);
  return wrap;
}

function detailRow(label: string, value: Node): HTMLElement {
  const row = el("div", { class: "detail-row" });
  row.append(el("div", { class: "label" }, label));
  const v = el("div");
  v.append(value);
  row.append(v);
  return row;
}

// -------------------- Helpers --------------------

function severityLabel(s: string): string {
  if (s === "error") return "BLOCKING";
  if (s === "warning") return "WARNING";
  return s.toUpperCase();
}

function shortTitle(r: CheckResult): string {
  // Prefer the rule description as the title, falling back to the message.
  return r.description ?? r.message;
}

function toLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * Detail keys whose numeric-array values are semantic versions
 * (`[major, minor, patch]`) and read best as a single dotted chip (`1.26.0`)
 * rather than one stacked row per component. Other numeric arrays — e.g.
 * `invalidTypeIndexes` — are left as lists.
 */
const VERSION_KEYS = new Set([
  "actual",
  "latest",
  "required",
  "minVersion",
  "expected",
  "latestKnown",
  "minEngineVersion",
]);

function formatValueNode(v: unknown, key?: string): Node {
  if (typeof v === "string") return codeNode(v);
  if (typeof v === "number" || typeof v === "boolean") return codeNode(String(v));
  if (Array.isArray(v)) {
    if (
      key !== undefined &&
      VERSION_KEYS.has(key) &&
      v.length > 0 &&
      v.every((n) => typeof n === "number")
    ) {
      return codeNode(v.join("."));
    }
    return renderArrayList(v);
  }
  return codeNode(JSON.stringify(v, null, 2));
}

/**
 * Render an array of values as a stack of `<code>` rows. If the array is
 * long, show the first few entries and hide the rest behind a "Show N more"
 * toggle — this keeps rolled-up findings (e.g. dozens of macOS sidecar
 * files) compact in the initial report view without losing the full list.
 */
const INLINE_ARRAY_LIMIT = 5;

function renderArrayList(items: unknown[]): Node {
  const frag = document.createDocumentFragment();
  const renderItem = (item: unknown): HTMLElement => {
    const row = document.createElement("div");
    row.className = "list-row";
    row.appendChild(codeNode(typeof item === "string" ? item : JSON.stringify(item)));
    return row;
  };

  const visible = items.slice(0, INLINE_ARRAY_LIMIT);
  for (const item of visible) frag.appendChild(renderItem(item));

  const hidden = items.slice(INLINE_ARRAY_LIMIT);
  if (hidden.length === 0) return frag;

  const more = document.createElement("div");
  more.className = "list-hidden";
  for (const item of hidden) more.appendChild(renderItem(item));
  more.hidden = true;

  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "list-toggle";
  toggle.setAttribute("aria-expanded", "false");
  const showText = `Show ${hidden.length} more`;
  const hideText = `Show fewer`;
  toggle.textContent = showText;
  toggle.addEventListener("click", () => {
    const expanded = !more.hidden;
    more.hidden = expanded;
    toggle.textContent = expanded ? showText : hideText;
    toggle.setAttribute("aria-expanded", expanded ? "false" : "true");
  });

  frag.appendChild(more);
  frag.appendChild(toggle);
  return frag;
}

function codeNode(value: string): HTMLElement {
  const c = document.createElement("code");
  c.textContent = value;
  return c;
}

function textNode(value: string): Text {
  return document.createTextNode(value);
}

function iconSvg(inner: string, size = 24, strokeWidth = 1.75): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
}

function el(tag: string, attrs: Record<string, string> = {}, text?: string): HTMLElement {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  if (text !== undefined) node.textContent = text;
  return node;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : "&quot;",
  );
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)} s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

export function shortSha(sha: string): string {
  if (!sha) return "n/a";
  if (sha.length <= 20) return sha;
  return `${sha.slice(0, 8)}…${sha.slice(-3)}`;
}
